// Bun inlines BUN_PUBLIC_* only when the var is set; otherwise `process` is left raw and undefined
// in the browser, so the reference is wrapped to fall back to the default.
function publicApiUrl(): string | undefined {
  try {
    return process.env.BUN_PUBLIC_API_URL
  } catch {
    return undefined
  }
}

export const API_BASE = publicApiUrl() || 'http://localhost:4000/api'

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * The request never got an answer: no network, the server is down, or the browser blocked the
 * response. Kept apart from ApiError (an answer) and from bugs in our own code, so only this one is
 * ever reported to a grower as "no connection".
 */
export class NetworkError extends Error {
  constructor(override readonly cause: unknown) {
    super('No connection to the server.')
    this.name = 'NetworkError'
  }
}

type Envelope<T> =
  | { success: true; data: T; page?: { nextCursor: string | null } }
  | { success: false; error: { code: string; message: string; details?: unknown; requestId: string } }

async function requestEnvelope<T>(path: string, init: RequestInit = {}): Promise<Extract<Envelope<T>, { success: true }>> {
  let res: Response
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...init,
      credentials: 'include',
      headers: { 'Content-Type': 'application/json', ...init.headers },
    })
  } catch (err) {
    throw new NetworkError(err)
  }
  const body = (await res.json().catch(() => null)) as Envelope<T> | null
  if (!body || !body.success) {
    const error = body && !body.success ? body.error : null
    throw new ApiError(error?.code ?? 'UNKNOWN', error?.message ?? `Request failed (${res.status})`, res.status, error?.details)
  }
  return body
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  return (await requestEnvelope<T>(path, init)).data
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  /** Like `get`, but for a cursor-paginated list endpoint: also returns `page.nextCursor`. */
  getPage: async <T>(path: string) => {
    const { data, page } = await requestEnvelope<T[]>(path)
    return { data, nextCursor: page?.nextCursor ?? null }
  },
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) => request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}

/** Drops null fields: create endpoints take omitted optionals, while PATCH takes null to clear. */
export function dropNulls<T extends object>(input: T): { [K in keyof T]: Exclude<T[K], null> } {
  return Object.fromEntries(Object.entries(input).filter(([, v]) => v !== null)) as { [K in keyof T]: Exclude<T[K], null> }
}
